import { invisiblyApi } from '../api/invisibly-api';
import { InvisiblyApiResponse } from '../../services/api/api-response.types';
import { APITagType } from '../api/invisibly-api.types';
import { Client, CreateClientRequest, ClientUserResponse, ClientUser, InviteCodeResponse, ClientAccessPlatform, ClientAccessRequest } from './organization.types';

const organizationEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizationClients: builder.query<Client[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/client',
      }),
      transformResponse(response: InvisiblyApiResponse<Client[]>) {
        return response.item;
      },
      providesTags: [{
        id: 'LIST',
        type: APITagType.OrganizationClients,
      }],
    }),
    getClientDetails: builder.query<Client, string>({
      query: (clientId) => ({
        method: 'get',
        url: `b2b/client/${clientId}`,
      }),
      transformResponse(response: InvisiblyApiResponse<Client>) {
        return response.item;
      },
      providesTags: (result, error, clientId) => [{
        id: clientId,
        type: APITagType.OrganizationClients,
      }],
    }),
    getClientUsers: builder.query<ClientUser[], string>({
      query: (clientId) => ({
        method: 'get',
        url: `b2b/client/${clientId}/user`,
      }),
      transformResponse(response: InvisiblyApiResponse<ClientUserResponse[]>) {
        return response.item
          .filter(user => user.status === 'active')
          .map(user => ({
            email: user.user.email,
            name: [user.user.first_name, user.user.last_name]
              .filter(Boolean)
              .join(' ') || '-',
            id: user.user.ext_user_id,
            is_client_admin: user.user.is_client_admin ?? false,
          }));
      },
      providesTags: (result, error, clientId) => [{
        id: clientId,
        type: APITagType.OrganizationClients,
      }],
    }),
    createClient: builder.mutation<Client, CreateClientRequest>({
      query: (data) => ({
        data,
        url: 'b2b/client',
        method: 'post',
      }),
      transformResponse(response: InvisiblyApiResponse<Client>) {
        return response.item;
      },
      invalidatesTags: [{
        id: 'LIST',
        type: APITagType.OrganizationClients,
      }],
    }),
    getClientInviteCode: builder.query<InviteCodeResponse, { clientId: string; forAdmin?: boolean }>({
      query: ({ clientId, forAdmin }) => ({
        method: 'get',
        url: `b2b/client/user/${clientId}/invite${forAdmin ? '?for_admin=True' : ''}`,
      }),
      transformResponse(response: InvisiblyApiResponse<InviteCodeResponse>) {
        return response.item;
      },
    }),
    updateClient: builder.mutation<Client, { clientId: string; data: Partial<Client> }>({
      query: ({ clientId, data }) => ({
        method: 'PATCH',
        url: `b2b/client/${clientId}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<Client>) {
        return response.item;
      },
      async onQueryStarted({ clientId, data }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedClient } = await queryFulfilled;
          dispatch(
            organizationEndpoints.util.updateQueryData('getClientDetails', clientId, (draft) => {
              Object.assign(draft, updatedClient);
            })
          );
          dispatch(
            organizationEndpoints.util.invalidateTags([{
              id: 'LIST',
              type: APITagType.OrganizationClients,
            }])
          );
        } catch (error) {
          console.error('Update client error:', error);
        }
      },
    }),
    getClientAccess: builder.query<ClientAccessPlatform[], string>({
      query: (clientId) => ({
        method: 'get',
        url: `b2b/client/${clientId}/access`,
      }),
      transformResponse(response: InvisiblyApiResponse<ClientAccessPlatform[]>) {
        return response.item;
      },
      keepUnusedDataFor: 0,
    }),
    updateClientAccess: builder.mutation<void, ClientAccessRequest>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/client/access',
        data,
      }),
    }),
    deleteClientAccess: builder.mutation<void, { ext_client_id: string; source: string; account: string; campaign: string }>({
      query: (data) => ({
        method: 'post',
        url: 'b2b/client/access/delete',
        data,
      }),
    }),
    deleteClientUser: builder.mutation<void, { clientId: string; userId: string }>({
      query: ({ clientId, userId }) => ({
        method: 'delete',
        url: `b2b/client/${clientId}/user/${userId}`,
      }),
      invalidatesTags: (result, error, { clientId }) => [{
        id: clientId,
        type: APITagType.OrganizationClients,
      }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetOrganizationClientsQuery,
  useGetClientDetailsQuery,
  useGetClientUsersQuery,
  useCreateClientMutation,
  useGetClientInviteCodeQuery,
  useUpdateClientMutation,
  useGetClientAccessQuery,
  useUpdateClientAccessMutation,
  useDeleteClientAccessMutation,
  useDeleteClientUserMutation,
} = organizationEndpoints;